import React, { useEffect, useState, useMemo } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import {
  Timer,
  Calculator,
  Loader2,
  Calendar,
  CalendarCheck,
} from 'lucide-react';
import {
  getDeliveryTimesReport,
  calculateAllEtas,
  getDeliveryTimesDetailed,
  backfillArrivalDates,
} from '../../lib/api';

function DeliveryTimesDetailModal({ open, onOpenChange }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, dateFrom, dateTo]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await getDeliveryTimesDetailed(params);
      setData(res.data.data);
    } catch (err) {
      console.error('Error loading detailed times:', err);
    } finally {
      setLoading(false);
    }
  };

  const getMonthPresets = () => {
    const presets = [];
    const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      presets.push({
        label: d.toLocaleDateString('es-MX', { month: 'short', year: 'numeric' }),
        from: d.toISOString().slice(0, 10),
        to: lastDay.toISOString().slice(0, 10)
      });
    }
    return presets;
  };

  const tabs = [
    { id: 'all', label: t('dashboard.tabSummary') },
    { id: 'client', label: t('dashboard.tabByClient') },
    { id: 'port', label: t('dashboard.tabByPort') },
    { id: 'carrier', label: t('dashboard.tabByCarrier') },
    { id: 'destination', label: t('dashboard.tabByDestination') },
    { id: 'trend', label: t('dashboard.tabTrend') },
  ];

  const renderDataList = (items, colorClass = 'text-cyan-400') => {
    if (!items || items.length === 0) {
      return <p className="text-center text-muted-foreground py-4">{t('dashboard.noDataAvailable')}</p>;
    }
    
    return (
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
        {items.map((item, idx) => (
          <div 
            key={idx} 
            className="flex items-center justify-between py-3 px-4 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors"
          >
            <div className="flex-1">
              <span className="text-sm font-medium text-foreground">{item.name || item.label || 'N/A'}</span>
              {item.category && (
                <span className="ml-2 text-xs text-muted-foreground">({item.category})</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <span className={`text-lg font-mono font-bold ${colorClass}`}>{item.avg_days}d</span>
                <p className="text-xs text-muted-foreground">{item.min_days}-{item.max_days} {t('dashboard.days')}</p>
              </div>
              <div className="w-16 text-right">
                <span className="text-xs text-muted-foreground">{item.sample_size} {t('dashboard.deliveriesCount')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderTrend = () => {
    const trend = data?.monthly_trend || [];
    if (trend.length === 0) {
      return <p className="text-center text-muted-foreground py-4">{t('dashboard.noDataAvailable')}</p>;
    }

    return (
      <div>
        <div className="h-64 mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} stroke="#334155" />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} stroke="#334155" />
              <Tooltip 
                content={({ active, payload, label }) => {
                  if (active && payload?.length) {
                    return (
                      <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-4 py-3 shadow-xl">
                        <p className="text-white font-semibold">{label}</p>
                        <p className="text-2xl font-heading text-cyan-400">{payload[0].value} {t('dashboard.days')}</p>
                        <p className="text-xs text-slate-400">{payload[0].payload.sample_size} {t('dashboard.deliveriesCount')}</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area 
                type="monotone" 
                dataKey="avg_days" 
                stroke="#06b6d4" 
                fill="url(#trendGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {renderDataList(trend, 'text-cyan-400')}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="text-cyan-400" size={24} />
            {t('dashboard.detailedAnalysis')}
          </DialogTitle>
          <DialogDescription>
            {t('dashboard.detailedAnalysisDesc')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-4 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">{t('dashboard.periodFilterLabel')}</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36 h-9 text-sm"
              placeholder={t('common.from')}
            />
            <span className="text-muted-foreground">—</span>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36 h-9 text-sm"
              placeholder={t('common.to')}
            />
          </div>
          <div className="flex gap-1">
            {getMonthPresets().map((preset, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setDateFrom(preset.from);
                  setDateTo(preset.to);
                }}
                className={`px-2 py-1 text-xs rounded-md transition-colors ${
                  dateFrom === preset.from && dateTo === preset.to
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary/50 text-muted-foreground hover:bg-secondary'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(''); setDateTo(''); }}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              {t('dashboard.clear')}
            </button>
          )}
        </div>

        {data?.overall && (
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-lg p-4 my-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('dashboard.periodAverage')}</p>
                <p className="text-4xl font-heading text-foreground">
                  {data.overall.avg_days || 0} <span className="text-xl text-muted-foreground">{t('dashboard.days')}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">{t('dashboard.range')}: {data.overall.min_days || 0} - {data.overall.max_days || 0} {t('dashboard.days')}</p>
                <p className="text-sm text-muted-foreground">{data.overall.sample_size || 0} {t('dashboard.deliveriesAnalyzed')}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-1 border-b border-border">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          ))}
        </div>

        <div className="py-4 overflow-y-auto max-h-[400px]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {activeTab === 'all' && (
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('dashboard.topClients')}</h4>
                    {renderDataList(data?.by_client?.slice(0, 5), 'text-cyan-400')}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">{t('dashboard.tabByPort')}</h4>
                    {renderDataList(data?.by_port?.slice(0, 5), 'text-blue-400')}
                  </div>
                </div>
              )}
              {activeTab === 'client' && renderDataList(data?.by_client, 'text-cyan-400')}
              {activeTab === 'port' && renderDataList(data?.by_port, 'text-blue-400')}
              {activeTab === 'carrier' && renderDataList(data?.by_carrier, 'text-purple-400')}
              {activeTab === 'destination' && renderDataList(data?.by_destination, 'text-emerald-400')}
              {activeTab === 'trend' && renderTrend()}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeliveryTimesWidget({ onCalculateEtas }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [reportRes, detailRes] = await Promise.all([
        getDeliveryTimesReport(),
        getDeliveryTimesDetailed(),
      ]);
      setData(reportRes.data);
      setTrend(detailRes.data?.data?.monthly_trend || []);
    } catch (err) {
      console.error('Error loading delivery times:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateEtas = async () => {
    setCalculating(true);
    try {
      const res = await calculateAllEtas();
      toast.success(res.data.message || t('dashboard.etaSuccess'));
      if (onCalculateEtas) onCalculateEtas();
      loadData();
    } catch (err) {
      toast.error(t('dashboard.etaError'));
      console.error(err);
    } finally {
      setCalculating(false);
    }
  };

  const handleBackfillDates = async () => {
    setBackfilling(true);
    const tid = toast.loading('Rellenando fechas de llegada...');
    try {
      const res = await backfillArrivalDates();
      const b = res.data?.backfill || {};
      toast.success(
        `${b.filled || 0} fecha(s) rellenada(s) · muestra ahora: ${res.data?.new_sample_size ?? '—'} entregas`,
        { id: tid, duration: 8000 }
      );
      loadData();
    } catch (err) {
      toast.error('Error al rellenar fechas de llegada', { id: tid });
      console.error(err);
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-card bg-card border border-border rounded-lg p-5">
        <h3 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
          <Timer className="text-cyan-400" size={20} />
          {t('dashboard.deliveryTimes')}
        </h3>
        <div className="text-center py-8 text-muted-foreground animate-pulse">{t('common.loading')}</div>
      </div>
    );
  }

  const overall = data?.overall || {};
  const byClient = data?.by_client || [];
  const byPort = data?.by_port || [];

  return (
    <>
      <div className="dashboard-card glass-widget rounded-lg p-5 opacity-0 animate-scale-in stagger-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-cyan-500/20 rounded-lg">
              <Timer className="text-cyan-400" size={18} />
            </div>
            <div>
              <h3 className="font-heading text-lg text-foreground">{t('dashboard.etaPredictionEngine')}</h3>
              <p className="text-xs text-muted-foreground">{data?.message || t('dashboard.basedOnHistorical')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBackfillDates}
              disabled={backfilling || calculating}
              title="Rellena las fechas de llegada en embarques ya entregados para que el motor sea exacto"
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-500/20 hover:bg-slate-500/30 text-slate-300 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="backfill-arrival-dates-btn"
            >
              {backfilling ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <CalendarCheck size={14} />
              )}
              {backfilling ? 'Rellenando...' : 'Rellenar fechas'}
            </button>
            <button
              onClick={handleCalculateEtas}
              disabled={calculating || backfilling}
              className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
              data-testid="calculate-etas-btn"
            >
              {calculating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Calculator size={14} />
              )}
              {calculating ? t('dashboard.calculating') : t('dashboard.calculateEtas')}
            </button>
          </div>
        </div>

        <button
          onClick={() => setShowDetailModal(true)}
          className="w-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 hover:from-cyan-500/20 hover:to-blue-500/20 rounded-lg p-4 mb-4 transition-all cursor-pointer group"
          data-testid="delivery-times-detail-btn"
        >
          <div className="flex items-center justify-between">
            <div className="text-left">
              <p className="text-sm text-muted-foreground">{t('dashboard.overallAverage')}</p>
              <p className="text-3xl font-heading text-foreground">
                {overall.avg_days || 0} <span className="text-lg text-muted-foreground">{t('dashboard.days')}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">{t('dashboard.range')}</p>
              <p className="text-sm text-foreground">{overall.min_days || 0} - {overall.max_days || 0} {t('dashboard.days')}</p>
              <p className="text-xs text-muted-foreground mt-1">{overall.sample_size || 0} {t('dashboard.samples')}</p>
            </div>
          </div>
          <p className="text-xs text-cyan-400 mt-2 opacity-0 group-hover:opacity-100 transition-opacity text-center">
            {t('dashboard.clickForDetail')} →
          </p>
        </button>

        {trend.length > 1 && (
          <div className="mb-4" data-testid="eta-trend-chart">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-muted-foreground">{t('dashboard.tabTrend')}</p>
              <p className="text-[10px] text-muted-foreground">{t('dashboard.avgDaysDescription')}</p>
            </div>
            <div className="h-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="widgetTrendGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#94a3b8' }} stroke="#334155" interval="preserveStartEnd" />
                  <YAxis hide domain={['dataMin - 2', 'dataMax + 2']} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload?.length) {
                        return (
                          <div className="bg-slate-900/95 border border-slate-700 rounded-lg px-3 py-2 shadow-xl">
                            <p className="text-white text-xs font-semibold">{label}</p>
                            <p className="text-lg font-heading text-cyan-400">{payload[0].value} {t('dashboard.days')}</p>
                            <p className="text-[10px] text-slate-400">{payload[0].payload.sample_size} {t('dashboard.deliveriesCount')}</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Area type="monotone" dataKey="avg_days" stroke="#06b6d4" fill="url(#widgetTrendGradient)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {byClient.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-medium text-muted-foreground mb-2">{t('dashboard.byClientTop5')}</p>
            <div className="space-y-1">
              {byClient.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-secondary/30 rounded-lg">
                  <span className="text-sm text-foreground truncate max-w-[150px]" title={item.client_name}>
                    {item.client_name || t('dashboard.noName')}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono font-medium text-cyan-400">{item.avg_days}d</span>
                    <span className="text-xs text-muted-foreground">({item.sample_size})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {byPort.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">{t('dashboard.byPort')}</p>
            <div className="space-y-1">
              {byPort.slice(0, 3).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 px-3 bg-secondary/30 rounded-lg">
                  <span className="text-sm text-foreground truncate max-w-[150px]" title={item.port}>
                    {item.port || t('dashboard.noPort')}
                  </span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono font-medium text-blue-400">{item.avg_days}d</span>
                    <span className="text-xs text-muted-foreground">({item.sample_size})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-center text-muted-foreground mt-4">
          {t('dashboard.avgDaysDescription')}
        </p>
      </div>

      <DeliveryTimesDetailModal 
        open={showDetailModal} 
        onOpenChange={setShowDetailModal}
      />
    </>
  );
}
